import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/styles';
import {
  Button,
  Dialog,
  Typography,
  colors,
  IconButton,
  useMediaQuery,
  Theme,
  List,
  TextField,
  Box,
  ListItem
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PublishIcon from '@material-ui/icons/Publish';
import Repositories from 'services/repositories';
import SharedRepoRecepientDTO from 'dto/SharedRepoRecepientDTO';
import { LoadingRing } from 'components/Loading/Loading';
import ImportDatasetModalItem from './ImportDatasetModalItem';
import DatasetDTO from 'dto/DatasetDTO';
import DataTableDialog from 'views/Repository/components/DataTableDialog';
import useSnackbar from 'utils/useSnackbar';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    overflow: 'hidden',
  },
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2)
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto'
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: colors.grey[100],
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between'
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center'
  },
  clearButton: {
    position: 'absolute',
    right: 10,
    top: 10,
    backgroundColor: '#fff'
  }
}));

interface ImportDatasetModalProps {
  open: boolean,
  onClose: () => void,
  className?: string,
  sharedRepos: SharedRepoRecepientDTO[]
  repositoryID: number
}

const ImportDatasetModal = (props: ImportDatasetModalProps) => {
  const { open, onClose, sharedRepos, className, repositoryID } = props;

  const theme = useTheme() as Theme;
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [checked, setChecked] = useState<Set<number>>(new Set<number>());
  const [loading, setLoading] = useState<boolean>();
  const [openDataset, setOpenDataset] = useState<DatasetDTO | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const handleAlert = useSnackbar();

  const handleClose = () => {
    onClose();
    setChecked(new Set<number>());
    setLoading(false);
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await Repositories.importDatasets(repositoryID, Array.from(checked));
      handleClose();
    } catch(err) {
      setLoading(false);
      handleAlert(err);
    }
  }

  useEffect(() => {
    if (open) {
      setChecked(new Set<number>());
    }
  }, [open])

  const filterdData = useMemo(() => {
    const data = [...sharedRepos];
    return data.filter((repo) => {
      const length = repo.datasets.filter((dataset) => {
        if(searchText.length > 0) {
          return dataset.name.toLowerCase().includes(searchText.toLowerCase())
        }
        return true;
      }).length
      return length > 0;
    }).map((repo) => {
      const r = {...repo};
      r.datasets = r.datasets.filter((dataset) => {
        if(searchText.length > 0) {
          return dataset.name.toLowerCase().includes(searchText.toLowerCase())
        }
        return true;
      })

      return r;
    })
  }, [sharedRepos, searchText]);

  const clearSearchInput = () => setSearchText('');

  return (
    <Dialog
      classes={{ paper: clsx(classes.root, className) }}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
    >
      <div className={classes.header}>
        <Typography
          variant="h3"
        >
          Select Datasets
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.main}>
        <Box p={2} width={'100%'} display={'block'} position={'relative'}>
          <TextField
            placeholder='Search Dataset'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)} 
            fullWidth 
          />
          {searchText.length > 0 && 
            <IconButton
              size='small'
              className={classes.clearButton}
              onClick={clearSearchInput}
            >
              <CloseIcon />
            </IconButton>
          }
        </Box>
        <Box p={1} style={{background: '#ccc'}}>
          <Typography variant='h6'>Repository Name</Typography>
        </Box>
        <List>
          {filterdData.length === 0 && 
            <ListItem
              divider
              disableGutters
            >
              <Box p={1} width={'100%'}>
                <Typography variant='h5' align='center'>
                  No records found
                </Typography>
              </Box>
            </ListItem>
          }
          {filterdData.map(r => {
            return <ImportDatasetModalItem
              defaultOpen={!!searchText.length}
              checked={checked}
              key={r.repoID}
              repo={r}
              setChecked={setChecked}
              setOpenDataset={setOpenDataset}
            />;
          })}
        </List>
      </div>
      <div className={classes.actions}>
        <div className={classes.flexRow}>
          {Boolean(checked.size) &&
            <Typography
              color="textSecondary"
              variant="subtitle1"
            >
              {checked.size} selected
            </Typography>
          }
        </div>
        <Button
          color="primary"
          disabled={!checked.size}
          endIcon={<PublishIcon />}
          onClick={onSubmit}
          variant="contained"
        >
          {loading ? <LoadingRing
            color='white'
          /> :'Import'}
        </Button>
      </div>
      <DataTableDialog
        dataset={openDataset}
        onClose={() => setOpenDataset(null)}
        open={!!openDataset}
        title=''
      />
    </Dialog>
  );
};

ImportDatasetModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool
};

export default ImportDatasetModal;
